import { Controller } from "@hotwired/stimulus"

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ""
}

export default class extends Controller {
  static values = {
    url: String,
    poll: { type: Boolean, default: false },
    sessionUrl: String,
    completeUrl: String,
    returnTo: String,
    launching: { type: Boolean, default: false },
    error: String,
  }

  static targets = [
    "status",
    "launchButton",
    "wizard",
    "wizardPhase1",
    "wizardPhase2",
    "wizardError",
    "otpWaiting",
    "otpDisplay",
    "otpCode",
    "copyBtn",
  ]

  connect() {
    // Pre-warm session + SDK so FB.login fires synchronously on click
    // (browsers block popups when FB.login is called after an await)
    this._sessionPromise = this.fetchSession()
      .then(session => this.ensureSdk(session).then(() => session))
      .catch(() => null)

    if (this.pollValue) {
      this.refresh()
      this.timer = window.setInterval(() => this.refresh(), 3000)
    }
  }

  disconnect() {
    if (this.timer) window.clearInterval(this.timer)
    this.stopOtpPolling()
    this.teardownMessageListener()
  }

  // ─── Wizard ─────────────────────────────────────────────────────────────

  openWizard(event) {
    event?.preventDefault()
    if (!this.hasWizardTarget) return

    // Reset to phase 1
    if (this.hasWizardPhase1Target) this.wizardPhase1Target.classList.remove("hidden")
    if (this.hasWizardPhase2Target) {
      this.wizardPhase2Target.classList.add("hidden")
      this.wizardPhase2Target.classList.remove("flex")
    }
    if (this.hasWizardErrorTarget) {
      this.wizardErrorTarget.textContent = ""
      this.wizardErrorTarget.classList.add("hidden")
    }
    // Reset OTP state for a fresh session
    if (this.hasOtpWaitingTarget) this.otpWaitingTarget.classList.remove("hidden")
    if (this.hasOtpDisplayTarget) this.otpDisplayTarget.classList.add("hidden")
    if (this.hasOtpCodeTarget) this.otpCodeTarget.textContent = ""

    this.wizardTarget.classList.remove("invisible", "pointer-events-none")

    // If an OTP was already captured (e.g. page reloaded mid-flow), surface it
    // immediately so the user can enter it without starting a new OAuth session.
    this.checkForExistingOtp()
  }

  closeWizard() {
    if (!this.hasWizardTarget) return
    this.wizardTarget.classList.add("invisible", "pointer-events-none")
  }

  backdropClick(event) {
    // Allow close only from phase 1 — in phase 2 the user needs to see the OTP
    if (this.hasWizardPhase2Target && !this.wizardPhase2Target.classList.contains("hidden")) return
    this.closeWizard()
  }

  // ─── Signup flow ─────────────────────────────────────────────────────────

  async launch(event) {
    event?.preventDefault()
    if (this.launchingValue) return

    this.launchingValue = true
    this.setStatus("")
    this.setButtonsDisabled(true)

    try {
      // Use pre-warmed session; fall back to fresh fetch if pre-warm failed.
      // We must resolve the session synchronously within the click handler so
      // that FB.login fires in the same tick as the user gesture — browsers
      // block popups the moment an await suspends execution.
      const session = (await this._sessionPromise) || (await this.fetchSession())

      // Refresh the promise so a subsequent click gets a fresh state token.
      this._sessionPromise = this.fetchSession()
        .then(s => this.ensureSdk(s).then(() => s))
        .catch(() => null)

      // ensureSdk is already done by connect(); this is a no-op if FB is ready.
      await this.ensureSdk(session)

      this.advanceToPhase2()
      this.startOtpPolling()

      try {
        const result = await this.runEmbeddedSignup(session)
        const completion = await this.completeSignup(session, result)
        this.closeWizard()
        if (completion.sendable) {
          window.location.assign(completion.return_to || this.returnToValue || window.location.href)
          return
        }
        window.location.reload()
      } finally {
        this.stopOtpPolling()
        this.advanceToPhase1()
      }
    } catch (error) {
      console.warn("[pro-whatsapp-signup] launch failed", error)
      this.setStatus(error.message || this.errorValue || "WhatsApp signup failed.")
    } finally {
      this.launchingValue = false
      this.setButtonsDisabled(false)
    }
  }

  advanceToPhase2() {
    if (this.hasWizardPhase1Target) this.wizardPhase1Target.classList.add("hidden")
    if (this.hasWizardPhase2Target) {
      this.wizardPhase2Target.classList.remove("hidden")
      this.wizardPhase2Target.classList.add("flex") // activates flex-col layout
    }
    if (this.hasOtpWaitingTarget) this.otpWaitingTarget.classList.remove("hidden")
    if (this.hasOtpDisplayTarget) this.otpDisplayTarget.classList.add("hidden")
    // Ensure wizard is visible (handles reopen case where wizard is closed)
    if (this.hasWizardTarget) this.wizardTarget.classList.remove("invisible", "pointer-events-none")
  }

  advanceToPhase1() {
    if (this.hasWizardPhase2Target) {
      this.wizardPhase2Target.classList.add("hidden")
      this.wizardPhase2Target.classList.remove("flex")
    }
    if (this.hasWizardPhase1Target) this.wizardPhase1Target.classList.remove("hidden")
  }

  // ─── SDK ─────────────────────────────────────────────────────────────────

  ensureSdk(session) {
    const appId = session.app_id
    const version = session.graph_version

    if (window.FB?.login) {
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version,
      })
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      const existing = document.getElementById("facebook-jssdk")
      window.fbAsyncInit = () => {
        try {
          window.FB.init({
            appId,
            autoLogAppEvents: true,
            xfbml: true,
            version,
          })
          resolve()
        } catch (error) {
          reject(error)
        }
      }

      if (existing) {
        if (window.FB?.init) {
          window.fbAsyncInit()
        }
        return
      }

      const script = document.createElement("script")
      script.id = "facebook-jssdk"
      script.async = true
      script.defer = true
      script.crossOrigin = "anonymous"
      script.src = "https://connect.facebook.net/en_US/sdk.js"
      script.onerror = () => reject(new Error("Could not load Facebook SDK."))
      document.body.appendChild(script)
    })
  }

  // ─── OTP polling ─────────────────────────────────────────────────────────

  async checkForExistingOtp() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!response.ok) return

      const data = await response.json()
      if (data.embedded_signup_otp) {
        // An OTP from a previous (or current) session is waiting — skip to phase 2
        // so the user can enter it into the still-open Meta popup without relaunching.
        this.advanceToPhase2()
        if (this.hasOtpCodeTarget) this.otpCodeTarget.textContent = data.embedded_signup_otp
        if (this.hasOtpWaitingTarget) this.otpWaitingTarget.classList.add("hidden")
        if (this.hasOtpDisplayTarget) this.otpDisplayTarget.classList.remove("hidden")
      }
    } catch {
      // ignore
    }
  }

  startOtpPolling() {
    this._otpTimer = window.setInterval(() => this.pollForOtp(), 3000)
  }

  stopOtpPolling() {
    if (this._otpTimer) {
      window.clearInterval(this._otpTimer)
      this._otpTimer = null
    }
  }

  async pollForOtp() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!response.ok) return

      const data = await response.json()
      if (data.embedded_signup_otp && this.hasOtpCodeTarget) {
        this.otpCodeTarget.textContent = data.embedded_signup_otp
        if (this.hasOtpWaitingTarget) this.otpWaitingTarget.classList.add("hidden")
        if (this.hasOtpDisplayTarget) this.otpDisplayTarget.classList.remove("hidden")
      }
    } catch {
      // ignore poll failures
    }
  }

  async copyOtp() {
    if (!this.hasOtpCodeTarget) return
    const code = this.otpCodeTarget.textContent.trim()
    if (!code) return

    try {
      await navigator.clipboard.writeText(code)
      if (this.hasCopyBtnTarget) {
        const btn = this.copyBtnTarget
        btn.innerHTML = `<svg class="h-4 w-4 text-positive" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 8l3.5 3.5 6.5-6.5"/></svg>`
        window.setTimeout(() => {
          btn.innerHTML = `<svg class="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="5" y="5" width="8" height="8" rx="1.5"/><path d="M11 5V3.5A1.5 1.5 0 009.5 2h-6A1.5 1.5 0 002 3.5v6A1.5 1.5 0 003.5 11H5"/></svg>`
        }, 2000)
      }
    } catch {
      // clipboard access denied — silently ignore
    }
  }

  // ─── Network ─────────────────────────────────────────────────────────────

  async fetchSession() {
    const response = await fetch(this.sessionUrlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      credentials: "same-origin",
    })

    const data = await response.json()
    if (!response.ok) {
      throw new Error(data.error || "Could not start WhatsApp signup.")
    }

    return data
  }

  runEmbeddedSignup(session) {
    return new Promise((resolve, reject) => {
      const assets = {
        wabaId: null,
        phoneNumberId: null,
        businessId: null,
        finishEvent: null,
      }
      let code = null

      const timeout = window.setTimeout(() => {
        this.teardownMessageListener()
        reject(new Error("WhatsApp signup timed out. Try again."))
      }, 10 * 60 * 1000)

      const maybeResolve = () => {
        if (!code) return
        if (!assets.finishEvent && !assets.wabaId) return

        const finishedWithoutPhone = assets.finishEvent === "FINISH_ONLY_WABA"
        if (!assets.phoneNumberId && !finishedWithoutPhone) return

        window.clearTimeout(timeout)
        this.teardownMessageListener()
        resolve({ ...assets, code })
      }

      this.onMessage = (event) => {
        if (!event.origin.endsWith("facebook.com")) return

        let data = event.data
        if (typeof data === "string") {
          try {
            data = JSON.parse(data)
          } catch {
            return
          }
        }

        if (data?.type !== "WA_EMBEDDED_SIGNUP") return

        if (data.event === "CANCEL") {
          window.clearTimeout(timeout)
          this.teardownMessageListener()
          reject(new Error(data.data?.error_message || "WhatsApp signup was cancelled."))
          return
        }

        if (data.event?.startsWith("FINISH")) {
          assets.wabaId = data.data?.waba_id || data.data?.waba_ids?.[0] || null
          assets.phoneNumberId = data.data?.phone_number_id || null
          assets.businessId = data.data?.business_id || null
          assets.finishEvent = data.event
          maybeResolve()
        }
      }

      window.addEventListener("message", this.onMessage)

      const extras = {
        featureType: "whatsapp_business_app_onboarding",
        sessionInfoVersion: "3",
        version: "v4",
        features: [],
        ...(session.solution_id ? { setup: { solutionID: session.solution_id } } : {}),
      }

      window.FB.login(
        (response) => {
          if (response.authResponse?.code) {
            code = response.authResponse.code
            maybeResolve()
            return
          }

          window.clearTimeout(timeout)
          this.teardownMessageListener()
          reject(new Error("Meta authorization was not completed."))
        },
        {
          config_id: session.config_id,
          response_type: "code",
          override_default_response_type: true,
          extras,
        }
      )
    })
  }

  async completeSignup(session, result) {
    const response = await fetch(this.completeUrlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      credentials: "same-origin",
      body: JSON.stringify({
        code: result.code,
        state: session.state,
        waba_id: result.wabaId,
        phone_number_id: result.phoneNumberId,
        business_id: result.businessId,
        finish_event: result.finishEvent,
      }),
    })

    const data = await response.json()
    if (!response.ok) {
      throw new Error(data.error || data.alert || "Could not save WhatsApp connection.")
    }

    return data
  }

  async refresh() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!response.ok) return

      const data = await response.json()
      if (data.active || data.failed) {
        window.clearInterval(this.timer)
        window.location.reload()
      }
    } catch (error) {
      console.warn("[pro-whatsapp-signup] poll failed", error)
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  teardownMessageListener() {
    if (!this.onMessage) return
    window.removeEventListener("message", this.onMessage)
    this.onMessage = null
  }

  setStatus(message) {
    if (this.hasWizardErrorTarget) {
      this.wizardErrorTarget.textContent = message
      this.wizardErrorTarget.classList.toggle("hidden", !message)
    }
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      this.statusTarget.classList.toggle("hidden", !message)
    }
  }

  setButtonsDisabled(disabled) {
    this.launchButtonTargets.forEach((button) => {
      button.disabled = disabled
    })
  }
}
